import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { uploadScreenshot, deleteScreenshot, formatApiError } from "../../lib/api";

const TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX = 4;
const MAX_BYTES = 5 * 1024 * 1024;

const clientError = (file, count) => {
  if (count >= MAX) return "You can add up to 4 screenshots — remove one to add another.";
  if (!TYPES.includes(file.type)) return "Only PNG, JPG or WEBP images are allowed.";
  if (file.size > MAX_BYTES) return "That image is over 5MB — please choose a smaller file.";
  return "";
};

export const ScreenshotManager = ({ token, shots, onChange, onUnauthorized }) => {
  const input = useRef(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handle = async (err) => (err.response?.status === 401 ? onUnauthorized() : setError(formatApiError(err.response?.data?.detail)));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setError("");
    setBusy(true);
    let count = shots.length;
    for (const file of files) {
      const msg = clientError(file, count);
      if (msg) { setError(`${file.name}: ${msg}`); break; }
      try {
        await uploadScreenshot(token, file);
        count += 1;
      } catch (err) {
        await handle(err);
        break;
      }
    }
    setBusy(false);
    onChange();
  };

  const remove = async (id) => {
    setError("");
    try {
      await deleteScreenshot(token, id);
    } catch (err) {
      await handle(err);
    }
    onChange();
  };

  return (
    <div data-testid="sneak-screenshots" className="mt-6 rounded-2xl border border-dashed border-[#E6DEC6] bg-[#FDFBF7] p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium text-[#2C221E]">Screenshots <span className="text-[#8A7A72]">({shots.length}/4)</span></p>
          <p className="text-xs text-[#8A7A72]">PNG, JPG or WEBP, up to 5MB each. Optional — the email works text-only too.</p>
        </div>
        <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={onFiles} data-testid="sneak-screenshot-input" />
        <button type="button" onClick={() => input.current?.click()} disabled={busy || shots.length >= MAX} data-testid="sneak-screenshot-add-button" className="btn-soft">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Add screenshot
        </button>
      </div>
      {error && <p role="alert" data-testid="sneak-screenshot-error" className="mt-3 text-sm font-medium text-[#B24E2A]">{error}</p>}
      {shots.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-4">
          {shots.map((s, i) => (
            <li key={s.id} data-testid="sneak-screenshot-item" className="relative">
              <img src={s.url} alt={`Screenshot ${i + 1}`} className="h-40 w-auto rounded-xl border border-[#E6DEC6] bg-white" />
              <button type="button" onClick={() => remove(s.id)} aria-label={`Remove screenshot ${i + 1}`} data-testid={`sneak-screenshot-remove-${i + 1}`}
                className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#2C221E] text-[#FDFBF7] hover:bg-[#C85A32] transition-colors">
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
